import { fabric } from 'fabric';
import _ from 'lodash';
import { DEFAULT_FONT } from '@/shared/ui';
import {
  convertBeatTypeNumberToText,
  isDeleteBeatMode,
} from '@/shared/ui/components/FabricJS/Helper';

type BeatIndicatorInstance = {
  type: string;
  canvas: fabric.Canvas;
  config: any;
  beatTypeNumber: number;
  x: number;
  // pre-existing: getJSONObject reads `xr`, which is never assigned - always undefined.
  xr?: number;
  line?: any;
  polyline?: any;
  beatText?: any;
  draw: () => void;
  update: (params: { point?: { x: number }; beatTypeNumber?: number; visible?: boolean }) => void;
  render: () => void;
  delete: () => void;
  getJSONObject: () => { type: string; position: number | undefined };
};
type BeatIndicatorCtor = new (
  cv: fabric.Canvas,
  config: any,
  beatTypeNumber: number,
) => BeatIndicatorInstance;

let BeatIndicatorImpl: any;

(function () {
  const STROKE_DELETE = {
    strokeWidth: 1,
    selectable: false,
    hasBorders: false,
    stroke: '#F23D42',
    originX: 'center',
    originY: 'center',
  };

  const STROKE_ADD = {
    strokeWidth: 1,
    selectable: false,
    hasBorders: false,
    stroke: '#0B68E5',
    originX: 'center',
    originY: 'center',
  };

  const BEAT_COLOR: Record<string, string> = {
    N: '#9DA7B2',
    S: '#E08789',
    V: '#E09663',
    Q: '#A097CC',
  };

  BeatIndicatorImpl = function (cv: fabric.Canvas, config: any, beatTypeNumber: number) {
    this.type = 'BeatIndicator';
    this.canvas = cv;
    this.config = config;
    this.beatTypeNumber = beatTypeNumber;
    this.x = -10;
  };

  BeatIndicatorImpl.prototype.draw = function () {
    const type = convertBeatTypeNumberToText(this.beatTypeNumber);
    this.line = new fabric.Line(
      [this.x, 1, this.x, this.config.height],
      isDeleteBeatMode(this.beatTypeNumber) ? STROKE_DELETE : STROKE_ADD,
    );
    this.line.id = 'bi';
    this.canvas.add(this.line);
    // Beat shape
    const startY = 12;
    this.polyline = new fabric.Polyline(
      [
        { x: 8, y: 0 },
        { x: 8, y: 10 },
        { x: 4, y: 14 },
        { x: 0, y: 10 },
        { x: 0, y: 0 },
      ],
      {
        top: startY,
        left: this.x,
        stroke: BEAT_COLOR[type],
        fill: BEAT_COLOR[type],
        selectable: false,
        strokeLineJoin: 'round',
        strokeWidth: 2,
        originX: 'center',
        originY: 'center',
        visible: !isDeleteBeatMode(this.beatTypeNumber),
      },
    );
    this.canvas.add(this.polyline);
    this.beatText = new fabric.IText(type, {
      fontFamily: DEFAULT_FONT,
      fontSize: 10,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
      // `selection` isn't in @types/fabric's ITextOptions but fabric.IText accepts it at runtime
      ...({ selection: false } as any),
      skipTargetFind: false,
      editable: false,
      width: 5,
      top: startY + 0.5,
      left: this.x,
      fill: 'white',
      originX: 'center',
      originY: 'center',
      visible: !isDeleteBeatMode(this.beatTypeNumber),
    });
    this.canvas.add(this.beatText);
    this.render();
  };

  BeatIndicatorImpl.prototype.update = function ({
    point,
    beatTypeNumber,
    visible,
  }: {
    point?: { x: number };
    beatTypeNumber?: number;
    visible?: boolean;
  }) {
    if (point) {
      this.x = point.x;
      this.line.set({ x1: this.x, x2: this.x });
      this.line.setCoords();
      this.polyline.set({ left: this.x });
      this.polyline.setCoords();
      this.beatText.set({ left: this.x });
      this.beatText.setCoords();
    }
    if (beatTypeNumber) {
      this.beatTypeNumber = beatTypeNumber;
      const type = convertBeatTypeNumberToText(this.beatTypeNumber);
      this.line.set({ ...(isDeleteBeatMode(this.beatTypeNumber) ? STROKE_DELETE : STROKE_ADD) });
      this.polyline.set({
        visible: !isDeleteBeatMode(this.beatTypeNumber),
        stroke: BEAT_COLOR[type],
        fill: BEAT_COLOR[type],
      });
      this.beatText.set({ text: type, visible: !isDeleteBeatMode(this.beatTypeNumber) });
      this.canvas.bringToFront(this.polyline);
      this.canvas.bringToFront(this.beatText);
    }
    if (!_.isNil(visible)) {
      this.polyline.set({ visible: visible ? !isDeleteBeatMode(this.beatTypeNumber) : false });
      this.beatText.set({ visible: visible ? !isDeleteBeatMode(this.beatTypeNumber) : false });
      this.line.set({ visible });
      this.canvas.bringToFront(this.polyline);
      this.canvas.bringToFront(this.beatText);
    }
    this.render();
  };

  BeatIndicatorImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  BeatIndicatorImpl.prototype.delete = function () {
    this.canvas.remove(this.line);
    this.canvas.remove(this.polyline);
    this.canvas.remove(this.beatText);
    this.line = null;
    this.polyline = null;
    this.beatText = null;
  };

  BeatIndicatorImpl.prototype.getJSONObject = function () {
    return {
      type: this.type,
      position: this.xr,
    };
  };
})();

const BeatIndicator: BeatIndicatorCtor = BeatIndicatorImpl;

export { BeatIndicator };
export default { BeatIndicator };
